import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/utils/seo";
import { getHotArticles, searchArticles } from "@/utils/articles";

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [sort, setSort] = useState<"relevance" | "time">("relevance");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setInput(query);
  }, [query]);

  useSeo({
    title: query ? `“${query}”的搜索结果 - 黑料` : "站内搜索 - 黑料",
    description: query
      ? `在黑料站内搜索“${query}”相关的娱乐八卦、明星黑料与社会热点内容，快速找到你关心的爆料。`
      : "输入关键词，在黑料站内搜索娱乐八卦、明星黑料与社会热点揭秘内容。",
    keywords: query ? `${query},黑料,娱乐八卦,明星黑料` : "黑料,站内搜索,娱乐八卦",
    canonicalPath: "/search",
  });

  const results = useMemo(() => {
    const list = searchArticles(query);
    if (sort === "time") {
      return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
    }
    return list;
  }, [query, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [query, sort]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = input.trim();
    if (!next) {
      setParams({});
      return;
    }
    setParams({ q: next });
  };

  const shown = results.slice(0, visible);
  const hasMore = visible < results.length;
  const hot = getHotArticles(6);

  return (
    <SiteLayout>
      <div className="px-4 md:px-6 py-6 md:py-10">
        <div className="rounded-lg bg-foreground-950 text-background-50 px-5 md:px-10 py-8 md:py-10 mb-6 md:mb-8">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl mb-3">站内搜索</h1>
          <p className="text-sm text-background-300 mb-5">
            输入明星、事件或关键词，快速定位你想要的黑料内容。
          </p>
          <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-2xl">
            <div className="flex items-center gap-2 flex-1 bg-background-50 text-foreground-950 rounded-md px-4 py-3">
              <i className="ri-search-line text-foreground-400" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="例如：恋情、换角、税务、直播"
                className="bg-transparent outline-none text-sm w-full"
                aria-label="搜索关键词"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>

        {query ? (
          <>
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <p className="text-sm text-foreground-600">
                关键词「<span className="font-bold text-foreground-950">{query}</span>」共找到{" "}
                <span className="font-bold text-primary-600">{results.length}</span> 条结果
              </p>
              <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-background-100">
                <button
                  type="button"
                  onClick={() => setSort("relevance")}
                  className={`px-3 py-1.5 rounded-full text-xs transition cursor-pointer whitespace-nowrap ${
                    sort === "relevance" ? "bg-background-50 font-medium" : "text-foreground-600"
                  }`}
                >
                  按相关度
                </button>
                <button
                  type="button"
                  onClick={() => setSort("time")}
                  className={`px-3 py-1.5 rounded-full text-xs transition cursor-pointer whitespace-nowrap ${
                    sort === "time" ? "bg-background-50 font-medium" : "text-foreground-600"
                  }`}
                >
                  按时间
                </button>
              </div>
            </div>

            {shown.length === 0 ? (
              <div className="flex flex-col items-center text-center py-16 border border-dashed border-background-300 rounded-lg">
                <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-4">
                  <i className="ri-search-eye-line text-3xl" />
                </span>
                <h2 className="font-heading font-bold text-lg mb-2">没有找到相关内容</h2>
                <p className="text-sm text-foreground-500 mb-6 max-w-md">
                  换个关键词试试，或者看看下面这些正在热传的爆料。
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-4-line" />
                  返回首页
                </Link>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 w-full text-left">
                  {hot.slice(0, 4).map((article) => (
                    <ArticleCard key={article.id} article={article} variant="horizontal" />
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {shown.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="horizontal" />
                  ))}
                </div>
                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition cursor-pointer whitespace-nowrap"
                    >
                      加载更多结果
                      <i className="ri-arrow-down-line" />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
                <i className="ri-fire-line" />
              </span>
              <h2 className="font-heading font-bold text-lg">大家都在看</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {hot.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}